import styles from "@/components/message-dialog.module.css";
import PopupShell from "@/components/popup-shell";
import { globals } from "@/globals";
import * as signals from "@/signals";
import { closeDialog } from "@/utils";
import { DialogTitle } from "@headlessui/react";
import { useSignalEffect } from "@preact/signals";
import { t } from "i18next";
import { JSX, MouseEvent, useRef } from "react";

function MessageDialog(): JSX.Element {
	const timeoutId = useRef<number | undefined>(undefined);

	const extraButtonElements = signals.dialog.value.extraButtons?.map(
		(button) => {
			const handleClick = (): void => {
				void globals.dialogCallbacks[button]();
				void closeDialog();
			};

			return (
				<button
					className="button"
					key={button}
					onClick={handleClick}
					type="button"
				>
					{t(button)}
				</button>
			);
		},
	);

	const handleCancelClick = (event: MouseEvent<HTMLButtonElement>): void => {
		void globals.dialogCallbacks.cancel?.(event);
		void closeDialog();
	};

	const handleOkClick = (event: MouseEvent<HTMLButtonElement>): void => {
		void globals.dialogCallbacks.ok?.(event);
		void closeDialog();
	};

	useSignalEffect(() => {
		window.clearTimeout(timeoutId.current);
		if (signals.dialog.value.countdown) {
			timeoutId.current = window.setTimeout(() => {
				const dialog = signals.dialog.value;
				signals.dialog.value = {
					...dialog,
					countdown:
						dialog.countdown && dialog.countdown > 0
							? dialog.countdown - 1
							: 0,
				};
			}, 1000);
		}
		return (): void => {
			window.clearTimeout(timeoutId.current);
		};
	});

	return (
		<PopupShell
			className="msg-box"
			isClosing={signals.isClosingDialog.value}
			isOpen={!!signals.dialog.value.text}
			onClose={closeDialog}
		>
			{signals.dialog.value.title && (
				<DialogTitle
					as="h1"
					className={styles["title"]}
				>
					{signals.dialog.value.title}
				</DialogTitle>
			)}
			<div className="msg-box-txt">
				{signals.dialog.value.isHtml && signals.dialog.value.text ? (
					<span
						dangerouslySetInnerHTML={{
							__html: signals.dialog.value.text,
						}}
					></span>
				) : (
					signals.dialog.value.text
				)}
			</div>
			<div className="button-container">
				{signals.dialog.value.showCancel && (
					<button
						className="button"
						type="button"
						onClick={handleCancelClick}
					>
						{t("cancel")}
					</button>
				)}
				{extraButtonElements}
				<button
					className="default-button"
					data-autofocus
					disabled={
						signals.dialog.value.disableOk ||
						!!signals.dialog.value.countdown
					}
					type="button"
					onClick={handleOkClick}
				>
					{t("ok")}
					{signals.dialog.value.countdown
						? ` (${signals.dialog.value.countdown})`
						: null}
				</button>
			</div>
		</PopupShell>
	);
}

export default MessageDialog;
